import { memo, ReactElement, useCallback, useState, ChangeEvent } from 'react';
import { useAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { App, Button, Pagination } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { createEditingJobNumberItem, jobNumbersAtomFamily } from '../atoms/settingAtoms';
import type { CommittedJobNumberItem } from '../types';
import { isDuplicateCommittedJobNumber } from '../utils/isDuplicateCommittedJobNumber';
import { selectJobNumberPage } from '../utils/selectJobNumberPage';
import JobNumberRow from './JobNumberRow';
import { ListHeader, JobNumberSearch, EmptyHint, PaginationFooter } from './styles';

interface Props {
  advertisementCode: string;
}

const PAGE_SIZE = 5;

const JobNumberList = memo(function JobNumberList({ advertisementCode }: Props): ReactElement {
  const { message } = App.useApp();
  const [rows, setRows] = useAtom(jobNumbersAtomFamily(advertisementCode));
  // 検索語・ページはこのリスト固有の一時的な表示状態のため useState で保持する。
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const editingRows = rows.filter(row => row.status === 'editing');
  const committedRows = rows.filter((row): row is CommittedJobNumberItem => row.status === 'committed');

  const { pageRows, currentPage, filteredCount, showSearch, showPagination } = selectJobNumberPage(
    committedRows,
    query,
    page,
    PAGE_SIZE
  );

  const handleSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setPage(1);
  }, []);

  const handleChangeJobNumber = useCallback(
    (id: string, value: string) => {
      setRows(prev => prev.map(row => (row.id === id ? { ...row, jobNumber: value } : row)));
    },
    [setRows]
  );

  // useAtomCallback で安定参照のコールバックにし、rows 依存による全行再レンダリングを避ける。
  // コールバックはイベント時に命令的に1回だけ実行されるため、最新 rows を get で読める。
  // 副作用(message.error)は updater の外で呼ぶ（updater は純粋関数であるべきため）。
  const handleCommit = useAtomCallback(
    useCallback(
      (get, set, id: string) => {
        const current = get(jobNumbersAtomFamily(advertisementCode));
        const target = current.find(row => row.id === id);
        if (target === undefined || target.status !== 'editing') return;
        const jobNumber = target.jobNumber.trim();
        if (jobNumber === '') return;
        if (isDuplicateCommittedJobNumber(current, id, jobNumber)) {
          void message.error(`仕事No「${jobNumber}」は既に登録されています`);
          return;
        }
        // MOCK [#239]: 本番実装時はバックエンドへの追加リクエストに置き換える。
        set(jobNumbersAtomFamily(advertisementCode), prev =>
          prev.map(row => (row.id === id ? { ...row, jobNumber, status: 'committed' } : row))
        );
      },
      [advertisementCode, message]
    )
  );

  const handleRemove = useCallback(
    (id: string) => {
      // MOCK [#239]: 本番実装時はバックエンドへの削除リクエストに置き換える。
      setRows(prev => prev.filter(row => row.id !== id));
    },
    [setRows]
  );

  const handleAppend = useCallback(() => {
    setRows(prev => [...prev, createEditingJobNumberItem()]);
  }, [setRows]);

  return (
    <>
      <ListHeader>
        <Button type='primary' icon={<PlusOutlined />} onClick={handleAppend}>
          新規作成
        </Button>
      </ListHeader>
      {showSearch && (
        <JobNumberSearch
          allowClear
          prefix={<SearchOutlined />}
          placeholder='仕事Noで検索'
          value={query}
          onChange={handleSearch}
          aria-label='仕事Noで検索'
        />
      )}
      {editingRows.map(row => {
        const trimmed = row.jobNumber.trim();
        const canCommit = trimmed !== '' && !isDuplicateCommittedJobNumber(rows, row.id, trimmed);
        return (
          <JobNumberRow
            key={row.id}
            item={row}
            canCommit={canCommit}
            onChangeJobNumber={handleChangeJobNumber}
            onCommit={handleCommit}
            onRemove={handleRemove}
          />
        );
      })}
      {pageRows.map(row => (
        <JobNumberRow
          key={row.id}
          item={row}
          canCommit={false}
          onChangeJobNumber={handleChangeJobNumber}
          onCommit={handleCommit}
          onRemove={handleRemove}
        />
      ))}
      {showSearch && filteredCount === 0 && <EmptyHint>該当する仕事Noがありません</EmptyHint>}
      {showPagination && (
        <PaginationFooter>
          <Pagination
            current={currentPage}
            pageSize={PAGE_SIZE}
            total={filteredCount}
            onChange={setPage}
            showSizeChanger={false}
            showTotal={total => `全${total.toLocaleString()}件`}
          />
        </PaginationFooter>
      )}
    </>
  );
});

export default JobNumberList;
